import { useState } from "react";
import { ethers } from "ethers";
import Form from "./Form";
import { FormStateProps, ErrorState } from "../common/tokenDeployer/types";
import { deployToken } from "../common/tokenDeployer/utils";
import { toast } from "react-toastify";
import WalletInfo from "../common/metamask/support-files/contractConnectedWallet";
import BCopy from "../../components/b-copy";
import { verifyContract } from "../common/tokenDeployer/utils/verifyContract";
import { ERC20 } from "../common/tokenDeployer/contracts/erc20/ERC20";

export default function ERC20Deployer() {
  const [formState, setFormState] = useState<FormStateProps>({
    name: "",
    symbol: "",
    supply: "",
  });

  const [isError, setIsError] = useState<ErrorState>({
    error: false,
    message: "",
    type: "",
  });

  const [walletAddress] = useState<string>("");

  const handleSubmit = async () => {
    const { name, symbol, supply } = formState;

    if (!name) {
      setIsError({
        error: true,
        message: "Token name is required.",
        type: "name",
      });
      return;
    }
    if (!symbol) {
      setIsError({
        error: true,
        message: "Token symbol is required.",
        type: "symbol",
      });
      return;
    }
    if (!supply) {
      setIsError({
        error: true,
        message: "Token supply is required.",
        type: "supply",
      });
      return;
    }

    setIsError({ error: false, message: "", type: "" });

    let loadingToast: number | string | null = null;

    try {
      if (!window.ethereum) {
        throw new Error("MetaMask not installed");
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const truncatedName =
        name.length > 30 ? name.substring(0, 30) + "..." : name;
      loadingToast = toast.loading(`Deploying ${truncatedName}...`);

      const contract = await deployToken({ name, symbol, supply, signer });
      const deployTx = contract.deploymentTransaction();

      if (!deployTx) {
        throw new Error("Deployment transaction not found");
      }

      // Wait for the deployment transaction to be mined
      await deployTx.wait();

      // Submit contract for verification
      await verifyContract(contract.target.toString(), ERC20);

      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      toast.success(
        <div>
          <p>{name} deployed successfully!</p>
          <a
            href={`/tx/${deployTx.hash}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#3b82f6", textDecoration: "underline" }}
          >
            View in Explorer
          </a>
          <p>
            Contract Address: {contract.target.toString()}{" "}
            <BCopy value={contract.target.toString()} />
          </p>
        </div>
      );
    } catch (error: any) {
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }

      
      if (
        error.code === 4001 ||
        error.code === "ACTION_REJECTED" ||
        error.message?.includes("user rejected") ||
        error.message?.includes("User rejected")
      ) {
        toast.error("Transaction cancelled by user");
        return;
      }

    
      const errorMessage = error.reason || error.message || "";
      const errorString = JSON.stringify(error).toLowerCase();
      const messageString = errorMessage.toLowerCase();
      
      if (
        error.code === -32000 ||
        error.code === -32603 ||
        messageString.includes("insufficient funds") ||
        messageString.includes("insufficient balance") ||
        messageString.includes("gas required exceeds allowance") ||
        messageString.includes("exceeds balance") ||
        messageString.includes("spendable balance") ||
        messageString.includes("insufficient balance for transfer") ||
        errorString.includes("insufficient funds") ||
        errorString.includes("insufficient balance")
      ) {
        toast.error("Insufficient funds in wallet. Please add funds to your wallet to complete the deployment.");
        return;
      }

      // Handle rate limiting
      if (
        error.code === -32000 && 
        (error.message?.includes("429") || messageString.includes("429") || errorString.includes("429"))
      ) {
        toast.error("Too many requests. Please wait a moment and try again.");
        return;
      }

      // Handle network errors
      if (
        messageString.includes("network") ||
        messageString.includes("connection") ||
        messageString.includes("timeout") ||
        error.code === "NETWORK_ERROR"
      ) {
        toast.error("Network error. Please check your connection and try again.");
        return;
      }

      
      const displayMessage = errorMessage || "An unknown error occurred";
      const truncatedError =
        displayMessage.length > 100
          ? displayMessage.substring(0, 100) + "..."
          : displayMessage;
      toast.error(`Deployment failed: ${truncatedError}`);
    }
  };

  return (
    <div className="td-container">
      <header className="td-header">
        <h1 className="td-title">Token Deployer</h1>
        <p className="td-subtitle">
          Easily deploy your ERC20 token on {process.env.REACT_APP_BASE_UNIT}{" "}
          Network.
        </p>
      </header>

      <section className="td-main-section">
        <WalletInfo />
        <Form
          isError={isError}
          formState={formState}
          setFormState={setFormState}
          walletAddress={walletAddress}
        />
        <button onClick={handleSubmit} className="td-deploy-button">
          Deploy
        </button>
      </section>

      <section className="td-info-section">
        <h2 className="td-section-title">The ERC20 Contract</h2>
        <p className="td-section-text">
          This token deployer creates a standard ERC20 token using
          OpenZeppelin's implementation. The deployed contract includes basic
          token functionality such as transfers, approvals, and allowances. The
          entire initial supply will be minted to the deployer's address.
        </p>
      </section>
    </div>
  );
}
